import { z } from 'zod';

/**
 * Liquidação de UM pagamento (#753, A4 do Asaas) — quanto o gateway cobrou de
 * taxa, quanto ficou líquido e para onde foi cada split.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200 (A4: extrato e comissões por split).
 *
 * Por quê: o owner decidiu (#748, G3) que parceiro e colaboradores recebem
 * sobre o LÍQUIDO. O doc `tenants/{t}/payments/{id}` só guarda o bruto, e não
 * pode guardar o resto: ele é lido por qualquer membro do tenant cliente
 * (`firestore.rules`, `match /payments/{paymentId}`), e taxa, carteira e
 * comissão não são do restaurante — mesma classe do achado #25 da auditoria da
 * #679 e do motivo de `/salesSplits` morar fora do tenant.
 *
 * Contrato: escrito só pelas Functions (webhook do Asaas, `invoice.paid` do
 * Stripe), em `/paymentSettlements/{tenantId}_{paymentId}`; lido pelo CRM
 * (`crmOnPaymentSettlementWritten`, `createCommissionsForPayment`) e pela
 * agregação diária. Rules: leitura só super-admin, escrita `false`.
 *
 * Usado por: Functions — `lib/billing/paymentSettlement.ts` (grava),
 * `billing/webhooks/stripeSettlement.ts` (grava, lado Stripe),
 * `aggregations/aggregateDailyMetrics.ts` (lê); crm-functions —
 * `crmOnPaymentSettlementWritten.ts`, `lib/commissions.ts`,
 * `lib/partnerClientAmounts.ts`, `lib/clientRows.ts`, `lib/splitCommissions.ts`
 * (só as constantes de status). `firestore.rules`: `match
 * /paymentSettlements/{settlementId}` — leitura `isSuperAdmin()`, escrita
 * `false`. Nenhum cliente (app/admin/CRM web) lê a coleção direto.
 *
 * Dados — o pagamento do spike A0 (#749), anonimizado: assinatura de
 * R$ 199,90 no cartão, parceiro a 20 %:
 *   { "tenant_id": "t-1", "payment_id": "pay_000000000001", "provider": "asaas",
 *     "livemode": false, "currency": "BRL", "gross_cents": 19990,
 *     "fee_cents": 646, "net_cents": 19344, "stripe_settlement": null,
 *     "splits": [{ "destination": "00000000-0000-4000-8000-000000000000",
 *                  "amount_cents": 3868, "status": "AWAITING_CREDIT" }],
 *     "source": "ASAAS_PAYMENT", "updated_at": 1790000000000 }
 *
 * Issues: #748 (épico), #753 (A4), #757 (Connect, A8).
 */

/**
 * Status de UM split no Asaas, como a cobrança o devolve (`payment.split[].status`).
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * Por quê: o spike A0 (#749, `docs/billing/asaas.md` item 4) mediu `PENDING` →
 * `AWAITING_CREDIT` (cartão confirmado) → `DONE` (liquidado); a doc pública
 * lista também `CANCELLED`, `REFUSED`, `REFUNDED` e o bloqueio por divergência.
 * `UNKNOWN` é o `.catch`: um status novo do Asaas não pode derrubar o parse do
 * settlement inteiro — a comissão do CRM depende dele.
 *
 * Usado por: `SettlementSplitSchema.status`; `CrmCommissionSchema.splitStatus`
 * (`crm.ts`), que mostra o status na linha informativa do extrato; Functions
 * `providers/asaasSchemas.ts` e `lib/billing/paymentSettlement.ts`. As
 * constantes (`SettlementSplitStatus.DONE`) são lidas em crm-functions
 * `lib/splitCommissions.ts`.
 */
export const SettlementSplitStatusSchema = z.enum([
  'PENDING',
  'AWAITING_CREDIT',
  'DONE',
  'CANCELLED',
  'REFUSED',
  'REFUNDED',
  'BLOCKED_BY_VALUE_DIVERGENCE',
  'UNKNOWN',
]);
export type SettlementSplitStatus = z.infer<typeof SettlementSplitStatusSchema>;
/**
 * Os status de split como constantes (`SettlementSplitStatus.DONE`).
 *
 * Origem: #753 — 2026-09-27 19:03 +0200 (padrão da #761: constante por conceito,
 * não o texto).
 */
export const SettlementSplitStatus = SettlementSplitStatusSchema.enum;

/**
 * Um split efetivo da cobrança: para qual destino foi e quanto.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * Contrato: `amount_cents` é o valor que o GATEWAY calculou (`totalValue` do
 * Asaas), não o nosso — o Asaas trunca (38,688 → 38,68) e `splitAmountCents`
 * arredonda. A linha informativa do extrato mostra este; o `MANUAL` usa o nosso.
 *
 * Usado por: só `PaymentSettlementSchema.splits[]` (este módulo). `status` é o
 * único `.catch()` do arquivo — ver `SettlementSplitStatusSchema`.
 */
export const SettlementSplitSchema = z.object({
  /** `walletId` Asaas (ou `acct_…` do Connect, na A8). */
  destination: z.string().min(1).max(128),
  amount_cents: z.number().int().nonnegative(),
  status: SettlementSplitStatusSchema.catch('UNKNOWN'),
});
export type SettlementSplit = z.infer<typeof SettlementSplitSchema>;

/**
 * De onde veio o líquido — para quem audita saber que conta foi feita.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * - `ASAAS_PAYMENT`: `netValue` e `split[]` da cobrança (`GET /payments/{id}`);
 * - `STRIPE_BALANCE_TRANSACTION`: `fee`/`net` da `balance_transaction` da
 *   charge, convertidos para a moeda da cobrança (ver `stripeNetInChargeCurrency`).
 *
 * Usado por: só `PaymentSettlementSchema.source` (este módulo). Sem `.catch()`
 * de propósito: fonte nova exige mexer no leitor, não passar despercebida.
 */
export const SettlementSourceSchema = z.enum(['ASAAS_PAYMENT', 'STRIPE_BALANCE_TRANSACTION']);
export type SettlementSource = z.infer<typeof SettlementSourceSchema>;

/**
 * `/paymentSettlements/{tenantId}_{paymentId}`.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * Contrato: `net_cents` e `fee_cents` estão SEMPRE na moeda da cobrança
 * (`currency`), porque é nela que a comissão é paga. No Stripe da PJ italiana a
 * `balance_transaction` vem em EUR mesmo numa cobrança em BRL; o valor original
 * fica em `stripe_settlement` para auditoria.
 *
 * Usado por: Functions `lib/billing/paymentSettlement.ts` (parse + grava),
 * `aggregations/aggregateDailyMetrics.ts`; crm-functions
 * `crmOnPaymentSettlementWritten.ts`, `lib/commissions.ts`,
 * `lib/partnerClientAmounts.ts`.
 *
 * Dados: ver o cabeçalho do módulo. `.default()` em `stripe_settlement` (null)
 * e `splits` ([]) cobre o doc do Asaas, que não tem o primeiro, e o do Stripe,
 * que não tem o segundo até a A8 — são "valor quando falta", não tolerância a
 * valor desconhecido.
 */
export const PaymentSettlementSchema = z.object({
  tenant_id: z.string().min(1).max(128),
  /** Id do doc em `tenants/{t}/payments` (`pay_…` no Asaas, `pi_…`/`in_…` no Stripe). */
  payment_id: z.string().min(1).max(128),
  provider: z.enum(['asaas', 'stripe']),
  livemode: z.boolean(),
  /** Moeda da COBRANÇA (ISO 4217, maiúscula). */
  currency: z.string().length(3),
  gross_cents: z.number().int().nonnegative(),
  fee_cents: z.number().int().nonnegative(),
  net_cents: z.number().int().nonnegative(),
  /** Só no Stripe: o que a `balance_transaction` disse, na moeda de liquidação. */
  stripe_settlement: z
    .object({
      currency: z.string().length(3),
      net_cents: z.number().int(),
      fee_cents: z.number().int().nonnegative(),
      /** `null` quando as moedas coincidem (o Stripe não manda taxa de câmbio). */
      exchange_rate: z.number().positive().nullable(),
    })
    .nullable()
    .default(null),
  /** Splits que o gateway aplicou. Vazio no Stripe até a A8 (#757). */
  splits: z.array(SettlementSplitSchema).max(8).default([]),
  source: SettlementSourceSchema,
  updated_at: z.number().int().nonnegative(),
});
export type PaymentSettlement = z.infer<typeof PaymentSettlementSchema>;

/**
 * Coleção raiz server-only das liquidações.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * Arquitetura: fora de `/tenants/{tenantId}/…` pelo mesmo motivo do
 * `SALES_SPLITS_COLLECTION` — nada aqui é do tenant cliente.
 *
 * Usado por: Functions `lib/billing/paymentSettlement.ts`,
 * `webhooks/stripeSettlement.ts`, `aggregations/aggregateDailyMetrics.ts`;
 * crm-functions `crmOnPaymentSettlementWritten.ts` (trigger `onDocumentWritten`
 * nesta coleção), `lib/commissions.ts`, `lib/clientRows.ts`.
 */
export const PAYMENT_SETTLEMENTS_COLLECTION = 'paymentSettlements';

/**
 * Id determinístico do settlement — idempotência de quem grava e chave de
 * quem lê a partir do pagamento.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * Por quê: o `tenantId` entra no id porque o id da cobrança só é único dentro
 * de um ambiente do gateway, e o id do doc de pagamento é do tenant.
 *
 * Usado por: Functions `lib/billing/paymentSettlement.ts`,
 * `webhooks/stripeSettlement.ts`, `aggregations/aggregateDailyMetrics.ts`;
 * crm-functions `lib/commissions.ts`. Mudar o formato deixa órfão tudo que já
 * foi gravado — o trigger do CRM não acharia o settlement do pagamento antigo.
 */
export function paymentSettlementId(tenantId: string, paymentId: string): string {
  return `${tenantId}_${paymentId}`;
}

/**
 * Líquido e taxa do Stripe na MOEDA DA COBRANÇA.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200 (decisão do owner: buscar a taxa
 * agora, pela `balance_transaction`).
 *
 * Por quê: a conta italiana liquida em EUR. Numa cobrança de R$ 199,90 a
 * `balance_transaction` diz `amount`/`fee`/`net` em EUR, com
 * `exchange_rate` = EUR por BRL. A comissão do parceiro BR é paga em BRL, então
 * o líquido volta para BRL pela mesma taxa que o Stripe usou: `net ÷ rate`. A
 * taxa em BRL é o bruto menos esse líquido — inclui a conversão cambial, que é
 * custo real da cobrança (decisão do owner: comissão sobre o líquido).
 *
 * Contrato: puro. `exchangeRate` `null` ⇒ mesma moeda (`net` já é da cobrança).
 * Líquido negativo (taxa maior que a cobrança) vira 0 — comissão sobre valor
 * negativo não existe. O líquido nunca passa do bruto.
 *
 * Usado por: Functions — `lib/billing/paymentSettlement.ts`; `paymentSettlement.test.ts`.
 */
export function stripeNetInChargeCurrency(args: {
  grossCents: number;
  settlementNetCents: number;
  exchangeRate: number | null;
}): { netCents: number; feeCents: number } {
  const raw =
    args.exchangeRate === null ? args.settlementNetCents : Math.round(args.settlementNetCents / args.exchangeRate);
  const netCents = Math.min(args.grossCents, Math.max(0, raw));
  return { netCents, feeCents: args.grossCents - netCents };
}
